import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { notifyOrgBrandingChanged } from '../api/org'
import { getProjects } from '../api/projects'
import {
  createEmbeddingProvider,
  createLlmProvider,
  createRerankProvider,
  listEmbeddingProviders,
  listLlmProviders,
  listProviderTemplates,
  listRerankProviders,
  testUnsavedEmbeddingProvider,
  testUnsavedLlmProvider,
  testUnsavedRerankProvider,
  updateEmbeddingProvider,
  updateLlmProvider,
  updateRerankProvider,
} from '../api/providers'
import {
  getSettings,
  listEmbeddingModels,
  listLlmModels,
  listRerankModels,
  updateSettings,
} from '../api/settings'
import { useDialog } from '../components/dialogs/useDialog'
import ModelPicker from '../components/ModelPicker'
import ProjectMembersPanel from '../components/ProjectMembersPanel'
import {
  EmbeddingProviderList,
  LlmProviderList,
  RerankProviderList,
} from '../components/ProviderLists'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import type {
  EmbeddingProviderEntry,
  LlmProviderEntry,
  ProjectResponse,
  ProviderTemplate,
  RerankProviderEntry,
  SettingsResponse,
} from '../types/api'

type SettingsTab = 'system' | 'providers' | 'mcp' | 'rest-api' | 'memory' | 'access' | 'appearance'

const VALID_SETTINGS_TABS: readonly SettingsTab[] = [
  'system',
  'providers',
  'mcp',
  'rest-api',
  'memory',
  'access',
  'appearance',
]

function isSettingsTab(value: string | null): value is SettingsTab {
  return VALID_SETTINGS_TABS.includes(value as SettingsTab)
}

const SETTINGS_TABS: Array<{ id: SettingsTab; label: string }> = [
  { id: 'system', label: 'System Settings' },
  { id: 'providers', label: 'Model Providers' },
  { id: 'mcp', label: 'MCP Server' },
  { id: 'rest-api', label: 'REST API' },
  { id: 'memory', label: 'Memory' },
  { id: 'access', label: 'Project Access' },
  { id: 'appearance', label: 'Appearance' },
]

const OPENROUTER_API_BASE_URL = 'https://openrouter.ai/api/v1'

const SHARED_ENDPOINT_PRESETS: Array<{ value: string; label: string }> = [
  { value: OPENROUTER_API_BASE_URL, label: 'OpenRouter' },
  { value: 'https://api.openai.com/v1', label: 'OpenAI' },
  { value: 'https://integrate.api.nvidia.com/v1', label: 'NVIDIA NIM' },
  { value: 'http://localhost:1234/v1', label: 'LM Studio' },
  { value: 'https://api.deepinfra.com/v1/openai', label: 'Deepinfra' },
]

type SharedAuthHeader = 'authorization_bearer' | 'api_key'

function isDeepInfraRerankUrl(url: string): boolean {
  return /deepinfra\.com/i.test(url) || /\{model(?:_name)?\}/i.test(url)
}

function sharedProviderLabel(prefix: string, kind: 'embedding' | 'LLM' | 'rerank'): string {
  return `${prefix} ${kind}`
}

/**
 * One shared connection (endpoint + key + auth header) with a separate
 * model field per kind. Creates or updates up to three providers — embedding,
 * LLM, and rerank — sharing the same endpoint credentials. Collapsed until
 * the user clicks "Add a new Shared Provider".
 */
function SharedConnectionForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void
  onCancel: () => void
}) {
  const dialog = useDialog()
  const [labelPrefix, setLabelPrefix] = useState('')
  const [apiBaseUrl, setApiBaseUrl] = useState(OPENROUTER_API_BASE_URL)
  const [apiKey, setApiKey] = useState('')
  const [authHeader, setAuthHeader] = useState<SharedAuthHeader>('authorization_bearer')
  const [embeddingModel, setEmbeddingModel] = useState('')
  const [llmModel, setLlmModel] = useState('')
  const [rerankModel, setRerankModel] = useState('')
  const [createEmbedding, setCreateEmbedding] = useState(true)
  const [createLlm, setCreateLlm] = useState(true)
  const [createRerank, setCreateRerank] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [existingEmbedding, setExistingEmbedding] = useState<EmbeddingProviderEntry[]>([])
  const [existingLlm, setExistingLlm] = useState<LlmProviderEntry[]>([])
  const [existingRerank, setExistingRerank] = useState<RerankProviderEntry[]>([])
  const [templates, setTemplates] = useState<ProviderTemplate[]>([])
  /** When set, create/test use per-service URLs from the chosen template. */
  const [pickedTemplate, setPickedTemplate] = useState<ProviderTemplate | null>(null)

  const reloadExisting = useCallback(async () => {
    const [embedding, llm, rerank] = await Promise.all([
      listEmbeddingProviders(),
      listLlmProviders(),
      listRerankProviders(),
    ])
    setExistingEmbedding(embedding)
    setExistingLlm(llm)
    setExistingRerank(rerank)
  }, [])

  useEffect(() => {
    let cancelled = false
    reloadExisting().catch(() => {
      if (!cancelled) {
        setExistingEmbedding([])
        setExistingLlm([])
        setExistingRerank([])
      }
    })
    return () => {
      cancelled = true
    }
  }, [reloadExisting])

  useEffect(() => {
    let cancelled = false
    listProviderTemplates()
      .then((rows) => {
        if (!cancelled) setTemplates(rows)
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const applyTemplate = (t: ProviderTemplate) => {
    setPickedTemplate(t)
    setApiBaseUrl(t.baseUrl)
    setLabelPrefix(t.name)
    if (t.embeddingModel) setEmbeddingModel(t.embeddingModel)
    if (t.chatModel) setLlmModel(t.chatModel)
    if (t.rerankModel) setRerankModel(t.rerankModel)
  }

  const trimmedUrl = apiBaseUrl.trim()
  const embeddingUrl = pickedTemplate?.embeddingUrl ?? trimmedUrl
  const chatUrl = pickedTemplate?.chatUrl ?? trimmedUrl
  const rerankUrl = pickedTemplate?.rerankUrl ?? trimmedUrl
  const prefixForMatch = labelPrefix.trim()
  const matchingEmbedding = prefixForMatch
    ? existingEmbedding.find(
        (p) =>
          p.label.trim().toLowerCase() ===
          sharedProviderLabel(prefixForMatch, 'embedding').toLowerCase()
      )
    : undefined
  const matchingLlm = prefixForMatch
    ? existingLlm.find(
        (p) =>
          p.label.trim().toLowerCase() === sharedProviderLabel(prefixForMatch, 'LLM').toLowerCase()
      )
    : undefined
  const matchingRerank = prefixForMatch
    ? existingRerank.find(
        (p) =>
          p.label.trim().toLowerCase() ===
          sharedProviderLabel(prefixForMatch, 'rerank').toLowerCase()
      )
    : undefined
  const updateTargets = (
    [
      createEmbedding && matchingEmbedding ? 'embedding' : null,
      createLlm && matchingLlm ? 'LLM' : null,
      createRerank && matchingRerank ? 'rerank' : null,
    ] as const
  ).filter((k): k is 'embedding' | 'LLM' | 'rerank' => k !== null)

  const handleTest = async () => {
    if (!trimmedUrl) {
      toast.error('Enter an API base URL first')
      return
    }
    setIsTesting(true)
    try {
      const results: string[] = []
      if (createEmbedding) {
        if (!embeddingModel.trim()) throw new Error('Embedding model is required for the test')
        const result = await testUnsavedEmbeddingProvider({
          provider: embeddingUrl.includes('openrouter.ai') ? 'openrouter' : 'openai_compatible',
          apiBaseUrl: embeddingUrl,
          apiKey: apiKey.trim() || null,
          modelName: embeddingModel.trim(),
          authHeader,
        })
        results.push(`embedding ok (${result.latencyMs} ms)`)
      }
      if (createLlm) {
        if (!llmModel.trim()) throw new Error('LLM model is required for the test')
        const result = await testUnsavedLlmProvider({
          apiBaseUrl: chatUrl,
          apiKey: apiKey.trim() || null,
          modelName: llmModel.trim(),
          authHeader,
        })
        results.push(`LLM ok (${result.latencyMs} ms)`)
      }
      if (createRerank) {
        if (!rerankModel.trim()) throw new Error('Rerank model is required for the test')
        const result = await testUnsavedRerankProvider({
          provider: isDeepInfraRerankUrl(rerankUrl)
            ? 'deepinfra'
            : rerankUrl.includes('openrouter.ai')
              ? 'openrouter'
              : 'openai_compatible',
          apiBaseUrl: rerankUrl,
          apiKey: apiKey.trim() || null,
          modelName: rerankModel.trim(),
          authHeader,
        })
        results.push(`rerank ok (${result.latencyMs} ms)`)
      }
      if (results.length === 0) throw new Error('Select at least one provider kind')
      toast.success(results.join(' · '))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connection test failed')
    } finally {
      setIsTesting(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!trimmedUrl) {
      toast.error('API base URL is required')
      return
    }
    if (updateTargets.length > 0) {
      const confirmed = await dialog.confirm({
        title: 'Update existing providers?',
        description: `Providers already exist for “${prefixForMatch}” (${updateTargets.join(', ')}). Update their shared connection details instead of creating duplicates?`,
        confirmLabel: 'Update providers',
        tone: 'default',
      })
      if (!confirmed) return
    }
    setIsSaving(true)
    try {
      const prefix = prefixForMatch || new URL(trimmedUrl).hostname
      const created: string[] = []
      const updated: string[] = []
      const notes: string[] = []
      if (createEmbedding) {
        if (!embeddingModel.trim()) throw new Error('Embedding model is required')
        if (matchingEmbedding) {
          await updateEmbeddingProvider(matchingEmbedding.id, {
            apiBaseUrl: embeddingUrl,
            apiKey: apiKey.trim() || null,
            authHeader,
            ...(pickedTemplate?.extraBody ? { extraBody: pickedTemplate.extraBody } : {}),
          })
          updated.push('embedding')
          if (
            matchingEmbedding.modelName &&
            matchingEmbedding.modelName !== embeddingModel.trim()
          ) {
            notes.push(
              `embedding model stays “${matchingEmbedding.modelName}” (model is locked after create)`
            )
          }
        } else {
          await createEmbeddingProvider({
            label: sharedProviderLabel(prefix, 'embedding'),
            provider: embeddingUrl.includes('openrouter.ai') ? 'openrouter' : 'openai_compatible',
            apiBaseUrl: embeddingUrl,
            apiKey: apiKey.trim() || null,
            modelName: embeddingModel.trim(),
            authHeader,
            ...(pickedTemplate?.extraBody ? { extraBody: pickedTemplate.extraBody } : {}),
            ...(/qwen/i.test(embeddingModel) ? { promptProfile: 'instruct' as const } : {}),
          })
          created.push('embedding')
        }
      }
      if (createLlm) {
        if (!llmModel.trim()) throw new Error('LLM model is required')
        if (matchingLlm) {
          await updateLlmProvider(matchingLlm.id, {
            apiBaseUrl: chatUrl,
            apiKey: apiKey.trim() || null,
            modelName: llmModel.trim(),
            authHeader,
            ...(pickedTemplate?.extraBody ? { extraBody: pickedTemplate.extraBody } : {}),
          })
          updated.push('LLM')
        } else {
          await createLlmProvider({
            label: sharedProviderLabel(prefix, 'LLM'),
            apiBaseUrl: chatUrl,
            apiKey: apiKey.trim() || null,
            modelName: llmModel.trim(),
            authHeader,
            ...(pickedTemplate?.extraBody ? { extraBody: pickedTemplate.extraBody } : {}),
          })
          created.push('LLM')
        }
      }
      if (createRerank) {
        if (!rerankModel.trim()) throw new Error('Rerank model is required')
        const rerankProvider = isDeepInfraRerankUrl(rerankUrl)
          ? 'deepinfra'
          : rerankUrl.includes('openrouter.ai')
            ? 'openrouter'
            : 'openai_compatible'
        if (matchingRerank) {
          await updateRerankProvider(matchingRerank.id, {
            apiBaseUrl: rerankUrl,
            apiKey: apiKey.trim() || null,
            modelName: rerankModel.trim(),
            authHeader,
            ...(pickedTemplate?.extraBody ? { extraBody: pickedTemplate.extraBody } : {}),
          })
          updated.push('rerank')
          if (matchingRerank.provider !== rerankProvider) {
            notes.push(
              `rerank provider type stays “${matchingRerank.provider}” (type is locked after create)`
            )
          }
        } else {
          await createRerankProvider({
            label: sharedProviderLabel(prefix, 'rerank'),
            provider: rerankProvider,
            apiBaseUrl: rerankUrl,
            apiKey: apiKey.trim() || null,
            modelName: rerankModel.trim(),
            authHeader,
            ...(pickedTemplate?.extraBody ? { extraBody: pickedTemplate.extraBody } : {}),
          })
          created.push('rerank')
        }
      }
      if (created.length === 0 && updated.length === 0) {
        throw new Error('Select at least one provider kind')
      }
      const parts: string[] = []
      if (created.length > 0) parts.push(`Created ${created.join(', ')}`)
      if (updated.length > 0) parts.push(`Updated ${updated.join(', ')}`)
      toast.success(parts.join(' · '))
      for (const note of notes) toast(note, { icon: 'ℹ️' })
      await reloadExisting()
      onCreated()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save providers')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleCreate}
      className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-harbor-text">Shared connection</h2>
          <p className="text-sm text-harbor-text-muted mt-1">
            One endpoint + API key for all three kinds — each kind keeps its own model. OpenRouter
            is pre-selected; switch presets, pick a shared template, or paste any OpenAI-compatible
            base URL.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 px-2.5 py-1.5 text-xs rounded-lg border border-harbor-border text-harbor-text-muted hover:text-harbor-text hover:bg-harbor-surface-raised"
        >
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="shared-label">
            Label prefix
          </label>
          <input
            id="shared-label"
            className="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            value={labelPrefix}
            onChange={(e) => setLabelPrefix(e.target.value)}
            placeholder="e.g. OpenRouter"
          />
        </div>
        <div>
          <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="shared-auth">
            Auth Header
          </label>
          <select
            id="shared-auth"
            className="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            value={authHeader}
            onChange={(e) =>
              setAuthHeader(e.target.value === 'api_key' ? 'api_key' : 'authorization_bearer')
            }
          >
            <option value="authorization_bearer">Authorization: Bearer (default)</option>
            <option value="api_key">api_key</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="shared-url">
            API base URL
          </label>
          <input
            id="shared-url"
            className="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            value={apiBaseUrl}
            onChange={(e) => {
              setPickedTemplate(null)
              setApiBaseUrl(e.target.value)
            }}
            required
            placeholder={OPENROUTER_API_BASE_URL}
            list="shared-url-presets"
          />
          <datalist id="shared-url-presets">
            {SHARED_ENDPOINT_PRESETS.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
            {templates.map((t) => (
              <option key={`tpl-${t.id}`} value={t.baseUrl}>
                {t.name}
              </option>
            ))}
          </datalist>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {SHARED_ENDPOINT_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => {
                  setPickedTemplate(null)
                  setApiBaseUrl(preset.value)
                }}
                className="px-2 py-0.5 text-[11px] rounded-full border border-harbor-border text-harbor-text-muted hover:text-harbor-text hover:border-harbor-border/80"
              >
                {preset.label}
              </button>
            ))}
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => applyTemplate(t)}
                className={`px-2 py-0.5 text-[11px] rounded-full border text-harbor-text-muted hover:text-harbor-text hover:border-harbor-border/80 ${
                  pickedTemplate?.id === t.id
                    ? 'border-emerald-500 text-emerald-300'
                    : 'border-harbor-border'
                }`}
                title={t.baseUrl}
              >
                {t.name}
              </button>
            ))}
          </div>
          {pickedTemplate && (
            <p className="text-[11px] text-harbor-text-muted mt-1">
              Using template “{pickedTemplate.name}” — embedding/chat/rerank URLs applied per
              service when creating.
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs text-harbor-text-secondary mb-1" htmlFor="shared-key">
            API key
          </label>
          <input
            id="shared-key"
            type="password"
            className="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-harbor-text">
          <input
            id="shared-create-embedding"
            type="checkbox"
            checked={createEmbedding}
            onChange={(e) => setCreateEmbedding(e.target.checked)}
            className="mt-8 accent-emerald-500"
          />
          <div className="flex-1 min-w-0">
            <ModelPicker
              apiBaseUrl={embeddingUrl}
              apiKey={apiKey}
              authHeader={authHeader}
              value={embeddingModel}
              onChange={setEmbeddingModel}
              inputClassName="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              disabled={!createEmbedding}
              id="shared-embedding-model"
              kind="embedding"
              loadModels={listEmbeddingModels}
              required={false}
              placeholder="e.g. qwen/qwen3-embedding-8b"
            />
          </div>
        </div>
        <div className="flex items-start gap-2 text-sm text-harbor-text">
          <input
            id="shared-create-llm"
            type="checkbox"
            checked={createLlm}
            onChange={(e) => setCreateLlm(e.target.checked)}
            className="mt-8 accent-emerald-500"
          />
          <div className="flex-1 min-w-0">
            <ModelPicker
              apiBaseUrl={chatUrl}
              apiKey={apiKey}
              authHeader={authHeader}
              value={llmModel}
              onChange={setLlmModel}
              inputClassName="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              disabled={!createLlm}
              id="shared-llm-model"
              kind="llm"
              loadModels={listLlmModels}
              required={false}
              placeholder="e.g. qwen/qwen3-32b"
            />
          </div>
        </div>
        <div className="flex items-start gap-2 text-sm text-harbor-text">
          <input
            id="shared-create-rerank"
            type="checkbox"
            checked={createRerank}
            onChange={(e) => setCreateRerank(e.target.checked)}
            className="mt-8 accent-emerald-500"
          />
          <div className="flex-1 min-w-0">
            <ModelPicker
              apiBaseUrl={rerankUrl}
              apiKey={apiKey}
              authHeader={authHeader}
              value={rerankModel}
              onChange={setRerankModel}
              inputClassName="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              disabled={!createRerank}
              id="shared-rerank-model"
              kind="rerank"
              loadModels={isDeepInfraRerankUrl(rerankUrl) ? undefined : listRerankModels}
              required={false}
              placeholder="e.g. Qwen/Qwen3-Reranker-4B"
            />
          </div>
        </div>
      </div>
      {updateTargets.length > 0 && (
        <p className="text-xs text-amber-400">
          Existing providers for this prefix ({updateTargets.join(', ')}) will be updated after
          confirmation — not duplicated.
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSaving || isTesting}
          className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
        >
          {isSaving
            ? 'Saving…'
            : updateTargets.length > 0
              ? 'Update providers'
              : 'Create providers'}
        </button>
        <button
          type="button"
          onClick={() => void handleTest()}
          disabled={isTesting || isSaving}
          className="px-3 py-1.5 text-sm rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50"
        >
          {isTesting ? 'Testing…' : 'Test'}
        </button>
      </div>
    </form>
  )
}

/**
 * One default per kind, side by side. Each kind's default is independent —
 * e.g. provider A can be the LLM default while provider B is the embedding
 * default — and every project without its own override follows these.
 */
function ProviderDefaultsSection() {
  const [embedding, setEmbedding] = useState<EmbeddingProviderEntry[]>([])
  const [llm, setLlm] = useState<LlmProviderEntry[]>([])
  const [rerank, setRerank] = useState<RerankProviderEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    Promise.all([listEmbeddingProviders(), listLlmProviders(), listRerankProviders()])
      .then(([e, l, r]) => {
        if (cancelled) return
        setEmbedding(e)
        setLlm(l)
        setRerank(r)
      })
      .catch(() => toast.error('Failed to load provider defaults'))
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSetDefault = async (kind: 'embedding' | 'llm' | 'rerank', id: number) => {
    const key = `${kind}-${id}`
    setIsSaving(key)
    try {
      if (kind === 'embedding') {
        await updateEmbeddingProvider(id, { isDefault: true })
        setEmbedding((rows) => rows.map((p) => ({ ...p, isDefault: p.id === id })))
        toast.success('Default embedding provider updated')
      } else if (kind === 'llm') {
        await updateLlmProvider(id, { isDefault: true })
        setLlm((rows) => rows.map((p) => ({ ...p, isDefault: p.id === id })))
        toast.success('Default LLM provider updated')
      } else {
        await updateRerankProvider(id, { isDefault: true })
        setRerank((rows) => rows.map((p) => ({ ...p, isDefault: p.id === id })))
        toast.success('Default rerank provider updated')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to set default')
    } finally {
      setIsSaving(null)
    }
  }

  const kinds = [
    { kind: 'embedding' as const, label: 'Embedding', rows: embedding },
    { kind: 'llm' as const, label: 'LLM', rows: llm },
    { kind: 'rerank' as const, label: 'Rerank', rows: rerank },
  ]

  return (
    <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-harbor-text">Defaults</h2>
        <p className="text-sm text-harbor-text-muted mt-1">
          One default per kind. Projects without their own override follow these.
        </p>
      </div>
      {isLoading ? (
        <p className="text-sm text-harbor-text-muted">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {kinds.map(({ kind, label, rows }) => {
            const current = rows.find((p) => p.isDefault) ?? null
            return (
              <div key={kind}>
                <label
                  className="block text-xs text-harbor-text-secondary mb-1"
                  htmlFor={`default-${kind}`}
                >
                  Default {label}
                </label>
                {rows.length === 0 ? (
                  <p className="text-xs text-harbor-text-muted py-2">
                    No {label.toLowerCase()} providers yet.
                  </p>
                ) : (
                  <select
                    id={`default-${kind}`}
                    className="w-full px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    value={current?.id ?? ''}
                    disabled={isSaving !== null}
                    onChange={(e) => {
                      const id = Number(e.target.value)
                      if (Number.isFinite(id) && id !== current?.id) void handleSetDefault(kind, id)
                    }}
                  >
                    {current === null && <option value="">Select default…</option>}
                    {rows
                      .filter((p) => p.isActive)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label} — {p.modelName ?? '—'}
                        </option>
                      ))}
                  </select>
                )}
                {current && (
                  <p className="text-[11px] text-harbor-text-muted mt-1 truncate">
                    {current.apiBaseUrl ?? '(local)'}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function SettingsPage() {
  const { user } = useAuth()
  const { theme, resolvedTheme, setTheme } = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const tab: SettingsTab = isSettingsTab(rawTab) ? rawTab : 'system'
  const setTab = useCallback(
    (next: SettingsTab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set('tab', next)
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const [settings, setSettings] = useState<SettingsResponse | null>(null)
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('')
  const [isLoading, setIsLoading] = useState(true)

  const [mcpRemoteEnabled, setMcpRemoteEnabled] = useState(true)
  const [isSavingMcp, setIsSavingMcp] = useState(false)

  const [restApiEnabled, setRestApiEnabled] = useState(true)
  const [isSavingRestApi, setIsSavingRestApi] = useState(false)

  const [memoryEnabled, setMemoryEnabled] = useState(false)
  const [memoryRestartRequired, setMemoryRestartRequired] = useState(false)
  const [isSavingMemory, setIsSavingMemory] = useState(false)

  const [globalEnrichmentEnabled, setGlobalEnrichmentEnabled] = useState(false)
  const [isSavingEnrichment, setIsSavingEnrichment] = useState(false)

  const [globalDistillationEnabled, setGlobalDistillationEnabled] = useState(false)
  const [isSavingDistillation, setIsSavingDistillation] = useState(false)

  const [textbookPipelineEnabled, setTextbookPipelineEnabled] = useState(true)
  const [textbookPipelineLocked, setTextbookPipelineLocked] = useState(false)
  const [textbookPipelineIsPlatformOrg, setTextbookPipelineIsPlatformOrg] = useState(false)
  const [isSavingTextbook, setIsSavingTextbook] = useState(false)

  const [rerankEnabled, setRerankEnabled] = useState(false)
  const [rerankCandidates, setRerankCandidates] = useState(30)
  const [isSavingRerank, setIsSavingRerank] = useState(false)
  const [providerRefreshKey, setProviderRefreshKey] = useState(0)
  const [sharedConnectionOpen, setSharedConnectionOpen] = useState(false)

  const loadSettings = useCallback(async () => {
    try {
      const data = await getSettings()
      setSettings(data)
      setMcpRemoteEnabled(data.mcpRemoteEnabled)
      setRestApiEnabled(data.restApiEnabled ?? true)
      setMemoryEnabled(data.memoryEnabled)
      setMemoryRestartRequired(data.memoryRestartRequired)
      setGlobalEnrichmentEnabled(data.contextualEnrichmentEnabled)
      setGlobalDistillationEnabled(data.transcriptDistillationEnabled ?? false)
      setTextbookPipelineEnabled(data.textbookPipelineEnabled ?? true)
      setTextbookPipelineLocked(data.textbookPipelineLocked ?? false)
      setTextbookPipelineIsPlatformOrg(data.textbookPipelineIsPlatformOrg ?? false)
      setRerankEnabled(data.rerankEnabled)
      setRerankCandidates(data.rerankCandidates)
    } catch {
      toast.error('Failed to load settings')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadSettings()
  }, [loadSettings])

  useEffect(() => {
    void getProjects()
      .then((data) => {
        setProjects(data)
        if (data.length > 0) {
          setSelectedProjectId((current) => (current === '' ? data[0].id : current))
        }
      })
      .catch(() => toast.error('Failed to load projects for member mapping'))
  }, [])

  const selectedProject = projects.find((p) => p.id === selectedProjectId)

  const handleMcpToggle = async (value: boolean) => {
    if (!settings) return
    setIsSavingMcp(true)

    const prevRemote = mcpRemoteEnabled
    setMcpRemoteEnabled(value)

    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        mcpRemoteEnabled: value,
      })
      setSettings(updated)
      toast.success(value ? 'Remote MCP enabled' : 'Remote MCP disabled')
    } catch {
      setMcpRemoteEnabled(prevRemote)
      toast.error('Failed to update MCP setting')
    } finally {
      setIsSavingMcp(false)
    }
  }

  const handleRestApiToggle = async (value: boolean) => {
    if (!settings) return
    setIsSavingRestApi(true)

    const prev = restApiEnabled
    setRestApiEnabled(value)

    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        restApiEnabled: value,
      })
      setSettings(updated)
      setRestApiEnabled(updated.restApiEnabled ?? value)
      // Refresh the sidebar (API Docs link) without a page reload.
      notifyOrgBrandingChanged()
      toast.success(value ? 'REST API enabled' : 'REST API disabled')
    } catch {
      setRestApiEnabled(prev)
      toast.error('Failed to update REST API setting')
    } finally {
      setIsSavingRestApi(false)
    }
  }

  const handleMemoryToggle = async (value: boolean) => {
    if (!settings) return
    setIsSavingMemory(true)

    const prev = memoryEnabled
    setMemoryEnabled(value)

    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        memoryEnabled: value,
      })
      setSettings(updated)
      setMemoryEnabled(updated.memoryEnabled)
      setMemoryRestartRequired(updated.memoryRestartRequired)
      toast.success(`Memory ${value ? 'enabled' : 'disabled'} — restart the API server to apply.`)
    } catch (error) {
      setMemoryEnabled(prev)
      toast.error(error instanceof Error ? error.message : 'Failed to update memory setting')
    } finally {
      setIsSavingMemory(false)
    }
  }

  const handleGlobalEnrichmentToggle = async (value: boolean) => {
    if (!settings) return
    setIsSavingEnrichment(true)
    const prev = globalEnrichmentEnabled
    setGlobalEnrichmentEnabled(value)
    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        contextualEnrichmentEnabled: value,
      })
      setSettings(updated)
      setGlobalEnrichmentEnabled(updated.contextualEnrichmentEnabled)
      const actuallyEnabled = updated.contextualEnrichmentEnabled
      if (value && !actuallyEnabled) {
        toast.error(
          'LLM settings are incomplete — enrichment requires a base URL, model name, and API key'
        )
      } else {
        toast.success(
          actuallyEnabled
            ? 'Global enrichment enabled — projects can now enable it'
            : 'Global enrichment disabled — all project enrichment is now off'
        )
      }
    } catch (error) {
      setGlobalEnrichmentEnabled(prev)
      toast.error(error instanceof Error ? error.message : 'Failed to update enrichment setting')
    } finally {
      setIsSavingEnrichment(false)
    }
  }

  const handleGlobalDistillationToggle = async (value: boolean) => {
    if (!settings) return
    setIsSavingDistillation(true)
    const prev = globalDistillationEnabled
    setGlobalDistillationEnabled(value)
    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        transcriptDistillationEnabled: value,
      })
      setSettings(updated)
      setGlobalDistillationEnabled(updated.transcriptDistillationEnabled ?? false)
      const actuallyEnabled = updated.transcriptDistillationEnabled ?? false
      if (value && !actuallyEnabled) {
        toast.error(
          'LLM settings are incomplete — distillation requires a base URL, model name, and API key'
        )
      } else {
        toast.success(
          actuallyEnabled
            ? 'Global transcript distillation enabled — projects can now opt in'
            : 'Global transcript distillation disabled'
        )
      }
    } catch (error) {
      setGlobalDistillationEnabled(prev)
      toast.error(error instanceof Error ? error.message : 'Failed to update distillation setting')
    } finally {
      setIsSavingDistillation(false)
    }
  }

  const handleTextbookPipelineToggle = async (value: boolean) => {
    if (!settings || textbookPipelineLocked) return
    setIsSavingTextbook(true)
    const prev = textbookPipelineEnabled
    setTextbookPipelineEnabled(value)
    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        textbookPipelineEnabled: value,
      })
      setSettings(updated)
      setTextbookPipelineEnabled(updated.textbookPipelineEnabled)
      setTextbookPipelineLocked(updated.textbookPipelineLocked)
      setTextbookPipelineIsPlatformOrg(updated.textbookPipelineIsPlatformOrg)
      toast.success(
        textbookPipelineIsPlatformOrg
          ? value
            ? 'Textbook pipeline enabled for all organizations (platform gate on)'
            : 'Textbook pipeline disabled platform-wide — org admins cannot enable it'
          : value
            ? 'Textbook pipeline enabled for this organization'
            : 'Textbook pipeline disabled for this organization'
      )
    } catch (error) {
      setTextbookPipelineEnabled(prev)
      toast.error(error instanceof Error ? error.message : 'Failed to update textbook setting')
    } finally {
      setIsSavingTextbook(false)
    }
  }

  const handleRerankToggle = async (value: boolean) => {
    if (!settings) return
    setIsSavingRerank(true)
    const prev = rerankEnabled
    setRerankEnabled(value)
    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        rerankEnabled: value,
      })
      setSettings(updated)
      setRerankEnabled(updated.rerankEnabled)
      toast.success(value ? 'Reranker enabled' : 'Reranker disabled')
    } catch (error) {
      setRerankEnabled(prev)
      toast.error(error instanceof Error ? error.message : 'Failed to update reranker setting')
    } finally {
      setIsSavingRerank(false)
    }
  }

  const handleRerankSave = async () => {
    if (!settings) return
    if (rerankCandidates < 1 || rerankCandidates > 100) {
      toast.error('Candidates must be between 1 and 100')
      return
    }
    setIsSavingRerank(true)
    try {
      const updated = await updateSettings({
        embeddingProvider: settings.embeddingProvider,
        rerankCandidates,
      })
      setSettings(updated)
      setRerankCandidates(updated.rerankCandidates)
      toast.success('Reranker candidates saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save reranker settings')
    } finally {
      setIsSavingRerank(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  const defaultInfo = settings?.defaultEmbedding

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-harbor-text">Settings</h1>
        <p className="text-harbor-text-secondary mt-1">Configure your ContextHarbor instance.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-harbor-border pb-2">
        {SETTINGS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              tab === t.id
                ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/30'
                : 'text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised border border-transparent'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'system' && (
        <div className="space-y-6">
          <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-2">
            <h2 className="text-lg font-semibold text-harbor-text">Base server URL</h2>
            <p className="text-sm text-harbor-text-secondary">
              Sourced from <code className="text-harbor-text-secondary">FRONTEND_URL</code> in the
              server&apos;s <code className="text-harbor-text-secondary">.env</code> file — the
              public URL of this deployment&apos;s Web UI, REST API, and remote MCP endpoint (
              <code className="text-harbor-text-secondary">/mcp</code>). The server syncs it into
              the settings store on every startup. Leave the loopback default (
              <code className="text-harbor-text-secondary">http://localhost:5173</code>) for
              local-only development.
            </p>
            {settings?.ragServerBaseUrl && (
              <p className="text-sm text-harbor-text-secondary">
                Effective base URL:{' '}
                <code className="text-harbor-text-secondary">{settings.ragServerBaseUrl}</code> (API
                at{' '}
                <code className="text-harbor-text-secondary">
                  {`${settings.ragServerBaseUrl.replace(/\/+$/, '')}/api`}
                </code>
                ).
              </p>
            )}
          </div>

          <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-harbor-text">
                LLM — Contextual enrichment (global)
              </h2>
              <p className="text-sm text-harbor-text-secondary mt-1">
                Global gate for LLM contextual enrichment. When enabled, each project can opt-in via
                its own Project Settings toggle. When disabled globally, no project can enable
                enrichment — the project toggle will be locked off.
              </p>
            </div>

            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-harbor-text">Enable enrichment globally</p>
                <p className="text-xs text-harbor-text-muted">
                  Requires an LLM provider configured in the LLM Provider tab. Project-level
                  enrichment only works when this is on.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={globalEnrichmentEnabled}
                disabled={isSavingEnrichment}
                onClick={() => handleGlobalEnrichmentToggle(!globalEnrichmentEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${globalEnrichmentEnabled ? 'bg-emerald-600' : 'bg-harbor-border'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${globalEnrichmentEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </label>
            {!globalEnrichmentEnabled && (
              <p className="text-xs text-amber-400/80">
                Global enrichment is off — all project enrichment is disabled regardless of
                per-project settings.
              </p>
            )}
            {globalEnrichmentEnabled &&
              settings &&
              !(settings.llmBaseUrl && settings.llmModelName) && (
                <p className="text-xs text-amber-400/80">
                  LLM settings are incomplete — enrichment will stay off until an LLM provider is
                  configured.
                </p>
              )}
          </div>

          <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-harbor-text">
                LLM — Transcript distillation (global)
              </h2>
              <p className="text-sm text-harbor-text-secondary mt-1">
                Global gate for transcript distillation. When enabled, each project can opt-in via
                its own Project Settings toggle. The LLM condenses transcript segments, drops
                filler, and extracts decisions and action items.
              </p>
            </div>

            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-harbor-text">
                  Enable transcript distillation globally
                </p>
                <p className="text-xs text-harbor-text-muted">
                  Requires an LLM provider configured in the LLM Provider tab.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={globalDistillationEnabled}
                disabled={isSavingDistillation}
                onClick={() => void handleGlobalDistillationToggle(!globalDistillationEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${globalDistillationEnabled ? 'bg-emerald-600' : 'bg-harbor-border'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${globalDistillationEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </label>
            {!globalDistillationEnabled && (
              <p className="text-xs text-amber-400/80">
                Global distillation is off — no project will auto-distill transcripts.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-harbor-text">Textbook pipeline</h2>
              <p className="text-sm text-harbor-text-secondary mt-1">
                {textbookPipelineIsPlatformOrg
                  ? 'Platform gate for all organizations. When off, no org can enable textbook page-based chunking.'
                  : 'Enable for this organization. Requires the platform gate on in the default organization.'}
              </p>
            </div>

            <label
              className={`flex items-center justify-between gap-4 ${textbookPipelineLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
            >
              <div>
                <p className="text-sm font-medium text-harbor-text">
                  {textbookPipelineIsPlatformOrg
                    ? 'Enable textbook pipeline (platform)'
                    : 'Enable textbook pipeline for this org'}
                </p>
                <p className="text-xs text-harbor-text-muted">
                  Takes effect immediately on the next ingest — no API restart required.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={textbookPipelineEnabled}
                disabled={isSavingTextbook || textbookPipelineLocked}
                onClick={() => void handleTextbookPipelineToggle(!textbookPipelineEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${textbookPipelineEnabled && !textbookPipelineLocked ? 'bg-emerald-600' : 'bg-harbor-border'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${textbookPipelineEnabled && !textbookPipelineLocked ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </label>
            {textbookPipelineLocked && (
              <p className="text-xs text-amber-400/80">
                Platform gate is off on the default organization — this org cannot enable textbook
                pipeline until a default-org admin turns it on.
              </p>
            )}
            {!textbookPipelineLocked && !textbookPipelineEnabled && (
              <p className="text-xs text-amber-400/80">
                Textbook uploads will use the reference profile instead of page-based textbook
                chunking.
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'providers' && (
        <div className="space-y-6" key={`providers-${providerRefreshKey}`}>
          {sharedConnectionOpen ? (
            <SharedConnectionForm
              onCreated={() => {
                setProviderRefreshKey((k) => k + 1)
                setSharedConnectionOpen(false)
              }}
              onCancel={() => setSharedConnectionOpen(false)}
            />
          ) : (
            <div className="rounded-xl border border-dashed border-harbor-border bg-harbor-surface/20 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-harbor-text">Shared connection</h2>
                <p className="text-sm text-harbor-text-muted mt-0.5">
                  Add embedding, LLM, and rerank providers that share one endpoint and API key.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSharedConnectionOpen(true)}
                className="shrink-0 px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                Add a new Shared Provider
              </button>
            </div>
          )}
          <ProviderDefaultsSection />
          <EmbeddingProviderList />
          <LlmProviderList />
          <RerankProviderList
            rerankEnabled={rerankEnabled}
            onToggleEnabled={handleRerankToggle}
            isSavingToggle={isSavingRerank}
            rerankCandidates={rerankCandidates}
            onCandidatesChange={setRerankCandidates}
            onSaveCandidates={() => void handleRerankSave()}
            isSavingCandidates={isSavingRerank}
          />
          {defaultInfo && settings?.localEmbeddingSupported !== false && (
            <div className="rounded-xl border border-harbor-border bg-harbor-surface/60 p-5 space-y-3">
              <h2 className="text-lg font-semibold text-harbor-text">Built-in default embedding</h2>
              <p className="text-sm text-harbor-text-secondary">{defaultInfo.description}</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-harbor-text-muted">Provider</dt>
                  <dd className="text-harbor-text font-medium">Local (Transformers.js)</dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted">Model</dt>
                  <dd className="text-harbor-text font-mono text-xs sm:text-sm">
                    {defaultInfo.modelName}
                  </dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted">Dimensions</dt>
                  <dd className="text-harbor-text">{defaultInfo.dimensions}</dd>
                </div>
                <div>
                  <dt className="text-harbor-text-muted">Your config matches default</dt>
                  <dd
                    className={
                      settings?.matchesDefaultEmbedding ? 'text-emerald-400' : 'text-amber-400'
                    }
                  >
                    {settings?.matchesDefaultEmbedding
                      ? 'Yes'
                      : 'No — re-index after switching models'}
                  </dd>
                </div>
              </dl>
              <div className="pt-2 border-t border-harbor-border">
                <h3 className="text-sm font-medium text-harbor-text-secondary mb-2">
                  Equivalent remote options
                </h3>
                <ul className="space-y-2 text-sm text-harbor-text-secondary">
                  {defaultInfo.equivalentProviderOptions.map((opt) => (
                    <li key={`${opt.provider}-${opt.modelName}`}>
                      <span className="text-harbor-text font-medium">{opt.provider}</span>
                      {' — '}
                      <span className="font-mono text-xs">{opt.modelName}</span>
                      <span className="block text-harbor-text-muted mt-0.5">{opt.note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'mcp' && (
        <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">MCP Server</h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Control whether the Streamable HTTP MCP endpoint is accessible to clients.
            </p>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-harbor-text">Remote MCP (HTTP)</p>
                <p className="text-xs text-harbor-text-muted">
                  Enable the Streamable HTTP endpoint at{' '}
                  <code className="text-harbor-text-secondary">/mcp</code> for external clients.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={mcpRemoteEnabled}
                disabled={isSavingMcp}
                onClick={() => handleMcpToggle(!mcpRemoteEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${
                  mcpRemoteEnabled ? 'bg-emerald-600' : 'bg-harbor-border'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    mcpRemoteEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </label>
          </div>
        </div>
      )}

      {tab === 'rest-api' && (
        <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">REST API</h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Control whether external apps and SDKs can call the REST API directly with an API key.
              The Web UI keeps working — it uses your login session, not API keys.
            </p>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between gap-4 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-harbor-text">Enable REST API</p>
                <p className="text-xs text-harbor-text-muted">
                  Allow direct REST API access via API keys (external apps, SDKs, scripts, and the
                  Swagger docs at <code className="text-harbor-text-secondary">/docs</code>). When
                  off, API-key requests are rejected and the API Docs link is hidden.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={restApiEnabled}
                disabled={isSavingRestApi}
                onClick={() => handleRestApiToggle(!restApiEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${
                  restApiEnabled ? 'bg-emerald-600' : 'bg-harbor-border'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    restApiEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </label>
            {!restApiEnabled && (
              <p className="text-xs text-amber-400/80">
                REST API is off — external API-key access and the Swagger docs are disabled. The Web
                UI is unaffected.
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'memory' && (
        <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">Memory (Mem0)</h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Durable, project-scoped memories (decisions, preferences, constraints) that agents
              record via <code className="text-harbor-text-secondary">search_memory</code> /{' '}
              <code className="text-harbor-text-secondary">add_memory</code> MCP tools. Requires a
              Postgres connection with pgvector.
            </p>
          </div>

          {memoryRestartRequired && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
              Saved. Restart the API server to apply — the memory layer initializes once at process
              startup and won&apos;t pick up this change until then.
            </div>
          )}

          <label className="flex items-center justify-between gap-4 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-harbor-text">Enable memory layer</p>
              <p className="text-xs text-harbor-text-muted">
                Advertises <code className="text-harbor-text-secondary">search_memory</code>,{' '}
                <code className="text-harbor-text-secondary">add_memory</code>, and{' '}
                <code className="text-harbor-text-secondary">forget_memory</code> over MCP, and
                enables the Memories tab on each project.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={memoryEnabled}
              disabled={isSavingMemory}
              onClick={() => handleMemoryToggle(!memoryEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 ${
                memoryEnabled ? 'bg-emerald-600' : 'bg-harbor-border'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  memoryEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </label>
        </div>
      )}

      {tab === 'access' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">Project Access</h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Map users to projects. All active admins are managers on every project by default.
            </p>
          </div>

          {projects.length === 0 ? (
            <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6 text-sm text-harbor-text-secondary">
              No projects yet. Create a project first, then assign members here.
            </div>
          ) : (
            <>
              <div>
                <label
                  htmlFor="settings-project"
                  className="block text-sm font-medium text-harbor-text-secondary mb-1"
                >
                  Project
                </label>
                <select
                  id="settings-project"
                  value={selectedProjectId}
                  onChange={(e) =>
                    setSelectedProjectId(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className="w-full max-w-md px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProject && selectedProjectId !== '' && (
                <ProjectMembersPanel
                  projectId={selectedProjectId}
                  projectRole={selectedProject.projectRole}
                  globalRole={user?.role}
                />
              )}
            </>
          )}
        </div>
      )}

      {tab === 'appearance' && (
        <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">Appearance</h2>
            <p className="text-sm text-harbor-text-secondary mt-1">
              Customize the look and feel of ContextHarbor.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-harbor-text mb-3">Theme</p>
              <div className="flex gap-3">
                {(['light', 'dark', 'system'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setTheme(option)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      theme === option
                        ? 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30'
                        : 'text-harbor-text-secondary border-harbor-border hover:text-harbor-text hover:bg-harbor-surface-raised'
                    }`}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-harbor-border">
              <p className="text-sm text-harbor-text-secondary">
                {resolvedTheme === 'dark' ? (
                  <>
                    <span className="text-harbor-text font-medium">Dark mode</span> is active. The
                    interface uses a dark color scheme optimized for low-light environments.
                  </>
                ) : (
                  <>
                    <span className="text-harbor-text font-medium">Light mode</span> is active. The
                    interface uses a light color scheme for bright environments.
                  </>
                )}
                {theme === 'system' && (
                  <span className="block text-xs text-harbor-text-muted mt-1">
                    Following system preference
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
